/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { getApplicationInsightsInstance } from 'src/core/services/telemetry/applicationInsights';
import { LogData, Logger } from 'src/core/services/telemetry/logger';
import { getUserSessionId } from 'src/core/utils/userSessionId';

/**
 * Returns implementation of Logger that uses application
 * insights instance to log events to tables in azure.
 */
export const getAppInsightLogger = (): Logger => {
    const appInsightsClient = getApplicationInsightsInstance();

    /**
     * Log a diagnostic info message
     */
    const info = (message: string, properties?: LogData) => {
        appInsightsClient.trackTrace({ message, severityLevel: SeverityLevel.Information, properties });
    };

    /**
     * Log a diagnostic warning message
     */
    const warn = (message: string, properties?: LogData) => {
        appInsightsClient.trackTrace({ message, severityLevel: SeverityLevel.Warning, properties });
    };

    /**
     * Log an exception caught
     */
    const error = (exception: Error, properties: LogData) => {
        appInsightsClient.trackException({
            exception,
            properties,
            severityLevel: SeverityLevel.Error
        });
    };

    /**
     * Log an event to CustomEvents table.
     * @param name The string you used to identify this event.
     * @param properties map[string, any] - additional data used to filter events. Defaults to empty.
     */
    const logEvent = (name: string, properties?: LogData) => {
        appInsightsClient.trackEvent({ name }, { ...properties });
    };

    /**
     * Start timing an extended event. Call `endEvent` to log the event when it ends.
     * @param name A string that identifies this event uniquely.
     */
    const startEvent = (name: string) => {
        appInsightsClient.startTrackEvent(name);
    };

    /**
     * Log an extended event that you started timing with `startEvent`.
     * @param name The string you used to identify this event in `startTrackEvent`.
     * @param properties map[string, string] - additional data used to filter events. Defaults to empty.
     */
    const endEvent = (name: string, failed: boolean, properties?: LogData) => {
        // stopTrackEvent is the only method in AppInsights that takes `[key: string]: string` for the
        // custom properties. All other methods takes `any` or `[key: string]: any`
        const stringProperties = Object.entries(properties).reduce((result, [key, value]) => {
            result[key] = `${value}`;

            return result;
        }, <Record<string, string>>{});

        if (failed) {
            stringProperties.failed = failed.toString();
        }

        appInsightsClient.stopTrackEvent(name, stringProperties);
    };

    /**
     * Logs that a page, or similar container was displayed to the user.
     * @param name A string that uniquely identifies this page.
     */
    const logPageView = (name: string) => {
        appInsightsClient.trackPageView({ name, properties: { userSessionId: getUserSessionId() } });
    };

    return {
        info,
        warn,
        error,
        logEvent,
        startEvent,
        endEvent,
        logPageView
    };
};
